import React from 'react';
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import { SecondaryButton, PrimaryButton } from '@/views/components/buttons';
import DropdownButton from '@/views/components/buttons/DropdownButton';
import { RateLimitType } from '@/redux/modules/types/rate-limits';
import RateLimitedComponent from '@/views/components/RateLimitedComponent';
import ComparisonShareButton from './ComparisonShareButton';
import { SHARED_CONTENT_TYPES } from '@/utils/constants';
import { userHasPermission, PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';
import Comparison from '@/types/comparison';
import User from '@/types/user';
import Nullable from '@/types/nullable';
import SharedComparisonInfos from '@/types/shared-comparison-infos';
import { canAccessUploadCSV } from '@/utils/feature-access';

interface ComparisonActionButtonsProps {
  user: User;
  comparison: Comparison;
  isLightVersion?: boolean;
  showPortfolioActions: boolean;
  portfolioActionsDisabled: boolean;
  onCreatePortfolio: () => void;
  showPortfolioUploadModal: () => void;
  openLoadClientPortfoliosModal: () => void;
  showLoadModelPortfoliosModal: () => void;
  onCreatePDFProposal: () => void;
  onSaveFromShare: () => Promise<boolean>;
  onShowExportPresentationModal: () => void;
  shareComparison: (infos: SharedComparisonInfos) => Promise<Nullable<string>>;
  shareIsDisabled: boolean;
  createProposalIsDisabled: boolean;
  isExporting?: boolean;
}

export const ComparisonActionButtons: React.FC<ComparisonActionButtonsProps> = ({
  user,
  comparison,
  isLightVersion,
  showPortfolioActions,
  portfolioActionsDisabled,
  onCreatePortfolio,
  showPortfolioUploadModal,
  openLoadClientPortfoliosModal,
  showLoadModelPortfoliosModal,
  onCreatePDFProposal,
  onSaveFromShare,
  onShowExportPresentationModal,
  shareComparison,
  shareIsDisabled,
  createProposalIsDisabled,
  isExporting,
}) => {
  if (!user) return null;

  const createDropdownItems = [
    {
      className: 'large-icon',
      node: formatMessage('Comparison.NewPortfolio'),
      disabled: !showPortfolioActions || portfolioActionsDisabled,
      icon: 'create-portfolio1',
      onClick: onCreatePortfolio,
    },
    {
      className: 'large-icon',
      node: formatMessage('Comparison.UploadCSVFile'),
      disabled: !showPortfolioActions || portfolioActionsDisabled,
      icon: 'upload-file',
      onClick: showPortfolioUploadModal,
      hidden: !canAccessUploadCSV(user),
    },
  ];

  const loadDropdownItems = [
    {
      node: formatMessage('Comparison.LoadClientPortfolio'),
      disabled: portfolioActionsDisabled,
      icon: 'existing-client-portfolio',
      onClick: openLoadClientPortfoliosModal,
    },
    {
      node: formatMessage('Comparison.LoadModelPortfolio'),
      disabled: portfolioActionsDisabled,
      icon: 'existing-model-portfolio',
      onClick: showLoadModelPortfoliosModal,
      hidden: !userHasPermission(user, PERMISSIONS.USER_MODELS) && !userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST),
    },
  ];

  const canExportOmni = userHasPermissionEnabled(user, PERMISSIONS.PROPOSAL_OMNI);

  return (
    <div className="d-flex">
      {userHasPermission(user, PERMISSIONS.COMPARISONS_SHARE) && (
        <ComparisonShareButton
          type={SHARED_CONTENT_TYPES.COMPARISON}
          comparison={comparison}
          disabled={shareIsDisabled}
          onShareComparison={shareComparison}
          onSaveChanges={onSaveFromShare}
          dark={!isLightVersion}
        />
      )}

      {showPortfolioActions && (
        <>
          <DropdownButton
            toggle={{ component: SecondaryButton, dark: !isLightVersion, icon: 'plus-med', className: 'secondary-v2 comparison-action-button' }}
            align="end"
            items={createDropdownItems}
          >
            <FormattedMessage id="Comparison.Create" />
          </DropdownButton>

          {userHasPermission(user, PERMISSIONS.PORTFOLIOS_LOAD) && (
            <DropdownButton
              toggle={{ component: SecondaryButton, dark: !isLightVersion, icon: 'upload-thin', className: 'secondary-v2 comparison-action-button' }}
              align="end"
              items={loadDropdownItems}
            >
              <FormattedMessage id="Comparison.Load" />
            </DropdownButton>
          )}
        </>
      )}

      <RateLimitedComponent type={RateLimitType.PROPOSALS}>
        {({ disabled, error }) => {
          const isButtonDisabled = disabled || createProposalIsDisabled;

          const commonProps = {
            className: 'primary-v2 create-proposal-button',
            disabled: isButtonDisabled,
            error,
            icon: isLightVersion ? 'upload-new' : 'download-thin',
          };

          if (canExportOmni && !isButtonDisabled) {
            return (
              <PrimaryButton onClick={onShowExportPresentationModal} loading={isExporting} {...commonProps}>
                <FormattedMessage id="ExportPresentationModal.Button" />
              </PrimaryButton>
            );
          }

          return (
            <PrimaryButton onClick={onCreatePDFProposal} {...commonProps}>
              <FormattedMessage id={canExportOmni ? 'Common.Export' : 'Comparison.CreateProposal'} />
            </PrimaryButton>
          );
        }}
      </RateLimitedComponent>
    </div>
  );
};
